import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { AlertCircle } from 'lucide-react';

export default function RiskDistributionChart({ data }) {
  const riskData = data?.risk_distribution || [];
  const healthStatus = data?.health_status || 'unknown';
  const highRiskPercentage = data?.high_risk_percentage || 0;

  // Color mapping for risk priorities
  const COLORS = {
    'mineur': '#10b981',
    'normal': '#3b82f6',
    'majeur': '#f59e0b',
    'critique': '#ef4444'
  };

  // Filter out risks with 0 count for better visualization
  const filteredData = riskData.filter(risk => risk.count > 0);

  // Custom label for pie chart
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="font-semibold"
        style={{ fontSize: '14px' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 capitalize mb-1">{data.name}</p>
          <p className="text-sm text-gray-600">Nombre: {data.value}</p>
          <p className="text-sm text-gray-600">Pourcentage: {data.payload.percentage}%</p>
        </div>
      );
    }
    return null;
  };

  // Health status badge
  const getHealthBadge = () => {
    if (healthStatus === 'healthy') {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
          Sain
        </div>
      );
    } else if (healthStatus === 'warning') {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-sm font-medium">
          <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
          Attention
        </div>
      );
    } else {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium">
          <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
          Critique
        </div>
      );
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Distribution des risques</h3>
          <p className="text-sm text-gray-600 mt-1">Par niveau de priorité</p>
        </div>
        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
          <AlertCircle className="h-5 w-5 text-orange-600" />
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-gray-600">État de santé</p>
          <div className="mt-1">{getHealthBadge()}</div>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">Risques élevés</p>
          <p className="text-2xl font-bold text-gray-900">{highRiskPercentage}%</p>
        </div>
      </div>

      {filteredData.length > 0 ? (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={filteredData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={100}
              fill="#8884d8"
              dataKey="count"
              nameKey="priority"
            >
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[entry.priority]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              formatter={(value) => <span className="capitalize text-sm">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[280px] flex items-center justify-center text-gray-500">
          <p>Aucun risque enregistré</p>
        </div>
      )}

      {/* Risk breakdown list */}
      <div className="mt-6 space-y-2">
        {riskData.map((risk, index) => (
          <div key={index} className="flex items-center justify-between py-2 border-t border-gray-100">
            <div className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-3"
                style={{ backgroundColor: COLORS[risk.priority] }}
              ></div>
              <span className="text-sm font-medium text-gray-700 capitalize">{risk.priority}</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">{risk.count} risque(s)</span>
              <span className="text-sm font-semibold text-gray-900">{risk.percentage}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}